import React, { useState } from 'react';
import { View, Image, TouchableOpacity, ScrollView } from 'react-native';
import { useStyles, createStyleSheet, UnistylesRuntime } from 'react-native-unistyles';
import RenderHtml from 'react-native-render-html';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faUser, faDownload } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import MyAppText from '../MyAppText';

interface ChatBubbleProps {
    comment: SupportTicketComment;
    user?: SupportTicketUser;
    isSupport?: boolean;
    isOwnMessage?: boolean;
    alignRight?: boolean;
}

interface AttachmentImageProps {
    attachment: SupportTicketCommentAttachment;
}

function RenderAttachmentImage({ attachment }: AttachmentImageProps) {
    const { styles, theme } = useStyles(styleSheet);
    const [hasError, setHasError] = useState(false);

    if (hasError || attachment.thumbnails.length === 0) {
        return (
            <View
                style={styles.thumbnailError}
            >
                <FontAwesomeIcon icon={faFile} color={theme.colors.iconColor} />
            </View>
        );
    }

    return (
        <Image
            style={styles.imgThumbnail}
            source={{ uri: attachment.thumbnails[0].url }}
            onError={() => setHasError(true)}
        />
    );
}

function formatFileSize(bytes: number): string {
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(0)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ChatBubble({ comment, user, isSupport = false, isOwnMessage = false, alignRight }: ChatBubbleProps) {
    const { styles, theme } = useStyles(styleSheet);
    const shouldAlignRight = alignRight !== undefined ? alignRight : isOwnMessage;

    return (
        <View style={[styles.container, shouldAlignRight && styles.containerRight]}>
            <View style={[styles.messageWrapper, shouldAlignRight && styles.messageWrapperRight]}>
                {!shouldAlignRight && (
                    <View style={styles.avatarContainer}>
                        {isSupport ? (
                            UnistylesRuntime.themeName === 'dark' ? (
                                <Image
                                    style={styles.userFrame}
                                    source={require('../../../assets/SentinelX_chat_dark.png')}
                                />
                            ) : (
                                <Image
                                    style={styles.userFrame}
                                    source={require('../../../assets/SentinelX_pin.png')}
                                />
                            )
                        ) : alignRight !== undefined ? (
                            <View style={styles.userInitialFrame}>
                                <MyAppText style={styles.userInitial}>
                                    {user?.name ? user.name[0].toUpperCase() : '?'}
                                </MyAppText>
                            </View>
                        ) : (
                            <View style={styles.userFrame}>
                                <FontAwesomeIcon icon={faUser} color="black" />
                            </View>
                        )}
                    </View>
                )}
                <View style={[styles.bubble, isOwnMessage && styles.bubbleOwn]}>
                    {!shouldAlignRight && (
                        (user?.name.includes('Sentinel') ? (
                            <MyAppText style={styles.sentinelName}>
                                {user?.name || comment.authorId}
                            </MyAppText>
                        ) : (
                            <MyAppText style={styles.userName}>
                                {user?.name || comment.authorId}
                            </MyAppText>
                        )
                        ))}

                    <View style={styles.contentWrapper}>
                        <RenderHtml
                            source={{ html: comment.content }}
                            baseStyle={{
                                color: theme.colors.labelColor,
                                margin: 0, padding: 0, alignItems: 'flex-end'
                            }}
                            tagsStyles={{
                                p: { marginTop: 0, marginBottom: 8 },
                            }}
                        />
                    </View>

                    {comment.attachments.length > 0 && (
                        <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            style={styles.attachmentsScrollView}
                        >
                            <View style={styles.attachmentsContainer}>
                                {comment.attachments.map((attachment) => (
                                    <TouchableOpacity
                                        style={styles.attachmentCard}
                                        key={String(attachment.id)}
                                        onPress={() => {
                                            window.open(attachment.url, '_blank');
                                        }}
                                    >
                                        <RenderAttachmentImage attachment={attachment} />
                                        {!attachment.contentType.startsWith('image/') && (
                                            <View style={styles.attachmentInfo}>
                                                <View style={styles.attachmentInfoRow}>
                                                    <View style={styles.attachmentInfoText}>
                                                        <MyAppText style={styles.attachmentName}>
                                                            {attachment.fileName}
                                                        </MyAppText>
                                                        <MyAppText style={styles.attachmentType}>
                                                            {attachment.size ? formatFileSize(attachment.size) : attachment.contentType.split('/').at(-1)?.toUpperCase()}
                                                        </MyAppText>
                                                    </View>
                                                    <FontAwesomeIcon icon={faDownload} color={theme.colors.iconColor} />
                                                </View>
                                            </View>
                                        )}
                                    </TouchableOpacity>
                                ))}
                            </View>
                        </ScrollView>
                    )}

                    <View style={styles.timestampContainer}>
                        <MyAppText style={styles.timestamp}>
                            {moment(comment.createdAt).format('HH:mm')}
                        </MyAppText>
                    </View>
                </View>

                {isSupport && shouldAlignRight && (
                    <View style={styles.avatarContainer}>
                        <View style={styles.userFrame}>
                            <FontAwesomeIcon icon={faUser} color="black" />
                        </View>
                    </View>
                )}
            </View>
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'row',
        width: '100%',
        paddingLeft: 6
    },
    containerRight: {
        justifyContent: 'flex-end',
    },
    messageWrapper: {
        maxWidth: '40%',
        flexDirection: 'row',
        gap: 8,
    },
    messageWrapperRight: {
        flexDirection: 'row-reverse',
    },
    avatarContainer: {
        paddingTop: 4,
    },
    userFrame: {
        width: 32,
        height: 32,
        borderRadius: 50,
        borderWidth: 1,
        borderColor: theme.colors.neutral[200],
        justifyContent: 'center',
        alignItems: 'center',
    },
    userInitialFrame: {
        width: 32,
        height: 32,
        borderRadius: 50,
        backgroundColor: theme.colors.chat.userAvatar,
        justifyContent: 'center',
        alignItems: 'center',
    },
    userInitial: {
        color: theme.colors.white[500],
        fontSize: 11,
        fontWeight: 'bold',
    },
    supportAvatar: {
        width: 32,
        height: 32,
        borderRadius: 50,
        justifyContent: 'center',
        alignItems: 'center',
    },
    bubble: {
        borderRadius: 8,
        backgroundColor: theme.colors.chat.bubble.sentinelBackground,
        padding: 8,
        flex: 1,
        borderColor: theme.colors.neutral[200],
        borderWidth: 1,
    },
    bubbleOwn: {
        backgroundColor: theme.colors.chat.bubble.background,
        borderColor: theme.colors.neutral[200],
        borderWidth: 1,
    },
    userName: {
        fontWeight: '700',
        fontSize: 14,
        marginBottom: 4,
        color: theme.colors.chat.userAvatar,
    },
    sentinelName: {
        fontWeight: '700',
        fontSize: 14,
        marginBottom: 4,
        color: theme.colors.chat.sentinelAvatar,
        // seus estilos para autor do comentário
    },
    contentWrapper: {
        marginBottom: 0,
    },
    timestampContainer: {
        alignItems: 'flex-end',
    },
    timestamp: {
        fontSize: 11,
        color: theme.colors.neutral[500],
        opacity: 0.6,
    },
    attachmentsScrollView: {
        marginTop: 8,
    },
    attachmentsContainer: {
        flexDirection: 'row',
        gap: 8,
    },
    attachmentCard: {
        borderColor: theme.colors.card.border,
        borderWidth: 1,
        borderRadius: 4,
        width: 134,
    },
    attachmentInfo: {
        padding: 8,
    },
    attachmentName: {
        fontSize: 12,
    },
    attachmentType: {
        fontSize: 12,
        color: theme.colors.neutral[400],
    },
    attachmentInfoRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
    },
    attachmentInfoText: {
        flex: 1,
    },
    thumbnailError: {
        backgroundColor: theme.colors.card.background,
        width: 132,
        height: 68,
        justifyContent: 'center',
        alignItems: 'center',
    },
    imgThumbnail: {
        objectFit: 'cover',
        width: 132,
        height: 68,
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
    }
}));
